"use client";

import { useEffect, useRef, useState } from "react";
import {
    AnimatePresence,
    motion,
    useInView,
    useReducedMotion,
} from "motion/react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Scores Lighthouse "avant" (site React/SPA) -> "après" (site Next.js).
const METRICS = [
    { labelKey: "Nextjs_seo_metric_performance", before: 42, after: 99 },
    { labelKey: "Nextjs_seo_metric_accessibility", before: 73, after: 100 },
    { labelKey: "Nextjs_seo_metric_bestpractices", before: 75, after: 100 },
    { labelKey: "Nextjs_seo_metric_seo", before: 64, after: 100 },
];

const scoreColor = (v) =>
    v >= 90 ? "text-emerald-400" : v >= 50 ? "text-amber-400" : "text-red-400";

// Jauge circulaire : le nombre et l'arc montent ensemble (compteur animé en rAF).
function ScoreGauge({ label, value, run }) {
    const reduce = useReducedMotion();
    const [display, setDisplay] = useState(0);
    const displayRef = useRef(0);
    const rafRef = useRef(null);

    useEffect(() => {
        displayRef.current = display;
    }, [display]);

    useEffect(() => {
        if (!run) return;
        if (reduce) {
            setDisplay(value);
            return;
        }
        const from = displayRef.current;
        const duration = 900;
        let start = null;
        const tick = (ts) => {
            if (start === null) start = ts;
            const p = Math.min(1, (ts - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
            setDisplay(Math.round(from + (value - from) * eased));
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, run, reduce]);

    const R = 26;
    const C = 2 * Math.PI * R;
    const dashOffset = C * (1 - display / 100);
    const color = scoreColor(value);

    return (
        <div className="flex flex-col items-center gap-2.5">
            <div
                className={`relative w-[72px] h-[72px] ${color} transition-colors duration-500`}
            >
                <svg
                    width="72"
                    height="72"
                    viewBox="0 0 64 64"
                    className="-rotate-90"
                >
                    <circle
                        cx="32"
                        cy="32"
                        r={R}
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="0.15"
                        strokeWidth="5"
                    />
                    <circle
                        cx="32"
                        cy="32"
                        r={R}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeDasharray={C}
                        strokeDashoffset={dashOffset}
                    />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold font-mono">
                    {display}
                </span>
            </div>
            <span className="text-[11px] sm:text-xs text-gray-300/80 text-center leading-tight max-w-[88px]">
                {label}
            </span>
        </div>
    );
}

export default function NextjsSeoScore() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-120px" });
    const [mode, setMode] = useState("before"); // before | after

    // À l'arrivée à l'écran : on montre d'abord le site React (scores faibles),
    // puis on bascule tout seul vers Next.js. L'utilisateur peut rejouer.
    useEffect(() => {
        if (!inView) return;
        if (reduce) {
            setMode("after");
            return;
        }
        setMode("before");
        const id = setTimeout(() => setMode("after"), 2200);
        return () => clearTimeout(id);
    }, [inView, reduce]);

    const isAfter = mode === "after";

    return (
        <section id="nextjs-seo" className="pt-16" ref={ref}>
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Nextjs_seo_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Nextjs_seo_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="magnifer-zoom-in-bold"
                            className="w-3.5 h-3.5"
                        />
                        {t("Nextjs_seo_window_label")}
                    </span>
                    <span
                        className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 transition-colors duration-500 ${
                            isAfter
                                ? "bg-emerald-500/15 ring-emerald-400/30 text-emerald-300"
                                : "bg-red-500/15 ring-red-400/30 text-red-300"
                        }`}
                    >
                        <SolarIcon
                            name={
                                isAfter
                                    ? "verified-check-bold"
                                    : "danger-triangle-bold"
                            }
                            className="w-3 h-3"
                        />
                        {isAfter
                            ? t("Nextjs_seo_verdict_after")
                            : t("Nextjs_seo_verdict_before")}
                    </span>
                </div>

                <div className="relative p-6 sm:p-9">
                    {/* Sélecteur React (SPA) / Next.js */}
                    <div className="flex justify-center mb-8">
                        <div className="inline-flex items-center gap-1 rounded-full bg-black/30 ring-1 ring-white/10 p-1">
                            <button
                                type="button"
                                onClick={() => setMode("before")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    !isAfter
                                        ? "bg-white/10 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Nextjs_seo_before_btn")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("after")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    isAfter
                                        ? "bg-sky-500 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Nextjs_seo_after_btn")}
                            </button>
                        </div>
                    </div>

                    {/* Jauges Lighthouse */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4 max-w-[520px] mx-auto">
                        {METRICS.map((m) => (
                            <ScoreGauge
                                key={m.labelKey}
                                label={t(m.labelKey)}
                                value={isAfter ? m.after : m.before}
                                run={inView}
                            />
                        ))}
                    </div>

                    {/* Aperçu dans Google */}
                    <div className="mt-9">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                            {t("Nextjs_seo_preview_label")}
                        </div>

                        <div className="relative rounded-2xl bg-white p-5 sm:p-6 shadow-xl min-h-[132px] overflow-hidden">
                            <AnimatePresence mode="wait">
                                {isAfter ? (
                                    <motion.div
                                        key="after"
                                        initial={
                                            reduce
                                                ? false
                                                : { opacity: 0, y: 8 }
                                        }
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={
                                            reduce
                                                ? { opacity: 0 }
                                                : { opacity: 0, y: -8 }
                                        }
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Image
                                                src="/static/iconx128.png"
                                                width={24}
                                                height={24}
                                                alt="Believemy"
                                                className="w-6 h-6 rounded-full"
                                            />
                                            <div className="leading-tight">
                                                <div className="text-[13px] text-[#202124] font-medium">
                                                    Believemy
                                                </div>
                                                <div className="text-[12px] text-[#4d5156]">
                                                    {t(
                                                        "Nextjs_seo_after_preview_url",
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <h4 className="text-[#1a0dab] text-lg sm:text-xl leading-snug hover:underline cursor-pointer">
                                            {t("Nextjs_seo_after_preview_title")}
                                        </h4>
                                        <div className="flex items-center gap-1 mt-1">
                                            <span className="flex items-center">
                                                {[0, 1, 2, 3, 4].map((i) => (
                                                    <SolarIcon
                                                        key={i}
                                                        name="star-bold"
                                                        className="w-3.5 h-3.5 text-amber-400"
                                                    />
                                                ))}
                                            </span>
                                            <span className="text-[12px] text-[#70757a] ml-1">
                                                {t(
                                                    "Nextjs_seo_after_preview_meta",
                                                )}
                                            </span>
                                        </div>
                                        <p className="text-[13px] sm:text-sm text-[#4d5156] leading-relaxed mt-1 mb-0">
                                            {t("Nextjs_seo_after_preview_desc")}
                                        </p>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="before"
                                        initial={
                                            reduce
                                                ? false
                                                : { opacity: 0, y: 8 }
                                        }
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={
                                            reduce
                                                ? { opacity: 0 }
                                                : { opacity: 0, y: -8 }
                                        }
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-400 text-[10px] font-bold">
                                                ?
                                            </span>
                                            <div className="text-[12px] text-[#4d5156]">
                                                {t(
                                                    "Nextjs_seo_before_preview_url",
                                                )}
                                            </div>
                                        </div>
                                        <h4 className="text-[#1a0dab] text-lg sm:text-xl leading-snug">
                                            {t("Nextjs_seo_before_preview_title")}
                                        </h4>
                                        <p className="text-[13px] sm:text-sm text-[#70757a] italic leading-relaxed mt-1 mb-0">
                                            {t("Nextjs_seo_before_preview_desc")}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Nextjs_seo_caption")}
            </p>
        </section>
    );
}
